/**
 * Auto-navigation (นำทาง): the player runs by themselves to a quest's next step, a person, a place or a hunting
 * ground, along the World's own walk (`moveTo` / `interact`: path.ts and the road graph, round obstacles, up stairs and
 * ramps, over bridges); out of a building by its door or stairs, into one by its door, and to the warp toward another
 * zone (the client sends the walk again in the next zone, docs/handoff/newdir/NAV.md). It runs (sprint) while stamina
 * lasts, never leaps or glides. At a person it turns to them and opens their talk (the World's `talkTo`).
 *
 * - `navTo` starts (or restarts) it, `navStop` ends it. Events `navStart`, `navArrive`, `navStop` (with the reason).
 * - It stops on the player's own movement or actions (any of `STOPS`), a skill, a blow taken, carrying a sack, death
 *   and a cutscene; when no way is found (`blocked`), or the goal is gone (`none`); and on leaving the zone (`travel`).
 * - Every half second it looks at its goal again (a person walks on, a beast is killed, a door is reached); between
 *   looks it only watches. No randomness, no allocation per tick beyond the World's own path.
 */
import { getMonster } from '../data/monsters';
import { MOUNTS } from '../data/mounts';
import { navPlace, type NavSpot } from '../data/navPlaces';
import { HUB, warpDistance, ZONES, type ZoneId } from '../data/zones';
import { DOOR_REACH, exteriorDoors, roomFeatures } from '../interiors';
import { isWaterAt } from '../qinggong';
import type { GameMap, Point, Region } from '../map';
import type { Entity, PlayerCommand, World, WorldEvent } from '../world';
import { stationOfZone, warpCost, walkSeconds, discoveredIds } from './fastTravel';
import { FAST_TRAVEL } from '../data/fastTravel';
import type { SystemHost, WorldSystem } from './types';

/** Where to go. `quest`: the current quest's next step, worked out by the World (`questTarget`). */
export type NavTarget =
  | { readonly kind: 'quest' }
  | { readonly kind: 'npc'; readonly id: number }
  /** A named place (data/navPlaces.ts). */
  | { readonly kind: 'place'; readonly place: string }
  | { readonly kind: 'point'; readonly x: number; readonly y: number }
  /** The nearest of a kind of monster (data/monsters.ts id), here or in the nearest zone that has it. */
  | { readonly kind: 'monster'; readonly monster: string }
  /** Into a building by its door (interiors.ts `exteriorDoors`). */
  | { readonly kind: 'building'; readonly building: string };

/** Commands (part of `PlayerCommand`). */
export type NavCommand = { t: 'navTo'; target: NavTarget } | { t: 'navStop' };

export type NavStopReason =
  /** `navStop`. */
  | 'cancel'
  /** The player moved, clicked, fought, jumped … themselves. */
  | 'input'
  | 'skill'
  /** Struck. */
  | 'combat'
  | 'sack'
  | 'death'
  | 'scene'
  /** No way there. */
  | 'blocked'
  /** Nothing to go to (no quest, the person is gone). */
  | 'none'
  /** Off through a warp toward the goal: the next zone carries on (the client sends the walk again). */
  | 'travel';

/** Events (part of `WorldEvent`). */
export type NavEvent =
  | { type: 'navStart'; target: NavTarget; label: string; x: number; y: number }
  | { type: 'navArrive'; label: string; x: number; y: number }
  | { type: 'navStop'; reason: NavStopReason };

/** What the walk is doing now. */
export type NavLeg = 'walk' | 'talk' | 'door' | 'exit' | 'stairs' | 'warp';

/** The walk under way, for the client's route and marker. */
export interface NavState {
  readonly target: NavTarget;
  /** The goal's name ("ผู้เฒ่าหลิว", "หมาป่าเงาจันทร์" …). */
  label: string;
  leg: NavLeg;
  /** Where this leg heads (the goal, or the door, stairs or warp on the way), on the map in play. */
  x: number;
  y: number;
}

/** The commands of the player's own that end the walk (`moveDir` only when it moves). */
const STOPS = new Set<PlayerCommand['t']>([
  'moveTo',
  'moveDir',
  'engage',
  'attack',
  'targetNearest',
  'interact',
  'dash',
  'jump',
  'meditate',
  'mount',
  'emote',
  'enter',
  'exit',
  'changeFloor',
  'travel',
  'blowPowder',
  'sackPickup',
  'gather',
]);

/** Seconds between looks at the goal. */
const THINK = 0.5;
/** A person counts as reached in talking range (the World's TALK_RANGE 1.9, and a little). */
const TALK_REACH = 2.2;
/** A place within this (m); a beast within this. */
const PLACE_REACH = 1.6;
const BEAST_REACH = 6;
/** The walk is aimed again once its goal has moved this far (m). */
const REAIM = 1.5;
/** No closer by this much (m) in this long (s): aim again; so many times running: no way there. */
const PROGRESS = 0.5;
const STALL_T = 4;
const STALL_TRIES = 3;
/** Run while stamina is over the high share, rest under the low one. */
const RUN_ON = 0.5;
const RUN_OFF = 0.15;
/** A leg longer than this (m) is ridden on an owned horse. */
export const MOUNT_LEG = 40;
/** The hint at this many warp hops. */
export const CARRIAGE_HOPS = 2;

/** What a target comes to on the map in play. */
type Goal =
  | { kind: 'npc'; e: Entity; label: string }
  | { kind: 'point'; x: number; y: number; within: number; label: string }
  | { kind: 'enter'; building: string; label: string }
  /** In another zone: the warp toward it. */
  | { kind: 'zone'; zone: ZoneId; label: string };

const centre = (r: Region): Point => ({ x: (r[0] + r[1]) / 2, y: (r[2] + r[3]) / 2 });

/** A place's spot on a map (the first of its `at` the map has), or null. */
export function spotOf(w: World, at: readonly NavSpot[]): Point | Entity | null {
  const m = w.map;
  for (const s of at) {
    if ('landmark' in s) {
      const p = m.landmarks?.[s.landmark];
      if (p) return p;
    } else if ('region' in s) return centre(m.regions[s.region]);
    else if ('lair' in s) return m.lair;
    else if ('arena' in s) return m.arena;
    else if ('plaza' in s) return m.plaza;
    else if ('elder' in s) return w.elder;
  }
  return null;
}

const SYSTEMS = new WeakMap<World, NavSystem>();

/** The World's navigation system (the client's route and marker, tests). */
export const navOf = (w: World): NavSystem | undefined => SYSTEMS.get(w);

export class NavSystem implements WorldSystem {
  readonly name = 'nav';
  /** The walk under way, or null. */
  state: NavState | null = null;
  private thinkT = 0;
  /** A command of the walk's own going through `World.apply` (not the player's). */
  private self = false;
  /** Whether the walk holds the run key (and lets it go when it ends); not once the player pressed it. */
  private running = false;
  private ownRun = true;
  /** The aim of the walk issued last, and the person talked to. */
  private aimX = NaN;
  private aimY = NaN;
  private talkId = -1;
  /** Progress: where the walker was last seen moving on from, how long since, and the stalls in a row. */
  private fromX = NaN;
  private fromY = NaN;
  private bestT = 0;
  private stalls = 0;
  private combatT = 0;
  /** The walk itself mounted the player (so it gets off at the goal or a door). */
  private rode = false;
  private hinted = false;

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  /** K31: opt-in; route through a paid warp when it saves more than 2 min of walking. */
  useWarp = false;

  private paidWarp(zone: ZoneId): boolean {
    const w = this.h.world;
    const ft = w.sys.fastTravel;
    const a = stationOfZone(w.map.zone);
    const b = stationOfZone(zone);
    if (!a || !b || !discoveredIds(w.progress).includes(b.id) || ft.blocked() || w.progress.gold < warpCost(a, b)) return false;
    if (walkSeconds(a, b) < FAST_TRAVEL.navSaving) return false;
    return ft.warp(b.id);
  }

  readonly commands = {
    navTo: (cmd: { target: NavTarget }) => this.start(cmd.target),
    navStop: () => this.stop('cancel'),
  };

  refuse(cmd: PlayerCommand): boolean {
    if (this.self || !this.state) return false;
    if (cmd.t === 'sprint') this.ownRun = false;
    else if (cmd.t === 'skill') this.stop('skill');
    else if (cmd.t === 'auto' ? cmd.on : STOPS.has(cmd.t) && (cmd.t !== 'moveDir' || !!cmd.dx || !!cmd.dy))
      this.stop('input');
    return false;
  }

  observe(e: WorldEvent): void {
    if (!this.state) return;
    const p = this.h.world.player;
    if (e.type === 'hit' && e.tgtId === p.id) this.stop('combat');
    else if ((e.type === 'death' || e.type === 'respawn') && e.id === p.id) this.stop('death');
    else if (e.type === 'travel') this.stop('travel');
  }

  step(dt: number): void {
    const st = this.state;
    if (!st) return;
    const w = this.h.world;
    const p = w.player;
    if (p.dead) return this.stop('death');
    if (w.scene) return this.stop('scene');
    if (p.carrying != null) return this.stop('sack');
    if (p.combatT > this.combatT + 1e-6) return this.stop('combat');
    this.combatT = p.combatT;
    this.run(p);
    if (w.interiors.busy) return;
    // Watch the walk: on the move (a path may lead away from its aim a while, up a flight), or stalled.
    if (Math.hypot(p.x - this.fromX, p.y - this.fromY) > PROGRESS) this.moved(p);
    else this.bestT += dt;
    this.thinkT -= dt;
    if (this.thinkT > 0) return;
    this.thinkT = THINK;
    this.think();
  }

  /** Starts a walk to a target (restarting one under way). */
  private start(target: NavTarget): void {
    const w = this.h.world;
    const p = w.player;
    if (p.dead) return;
    if (this.state) this.end();
    // Indoors the zone outside is set aside: the walk goes out first and looks for its goal there.
    const g = w.interiors.where && target.kind !== 'building' ? { label: 'ทางออก' } : this.resolve(target);
    if (!g) {
      this.h.notice(target.kind === 'quest' ? 'ไม่มีภารกิจให้นำทาง' : 'ไม่พบที่หมาย');
      return;
    }
    this.state = { target, label: g.label, leg: 'walk', x: p.x, y: p.y };
    this.thinkT = 0;
    this.stalls = 0;
    this.moved(p);
    this.combatT = p.combatT;
    this.ownRun = true;
    this.aimX = this.aimY = NaN;
    this.talkId = -1;
    this.hinted = false;
    this.think();
    const st = this.state as NavState | null;
    if (!st) return;
    this.h.emit({ type: 'navStart', target, label: st.label, x: st.x, y: st.y });
    this.h.notice(`นำทางไป${st.label}`);
  }

  /** Ends the walk, saying why. */
  private stop(reason: NavStopReason): void {
    if (!this.state) return;
    const p = this.h.world.player;
    // Let go of the walk itself when it is called off (a new command of the player's takes over anyway).
    if (reason === 'cancel' || reason === 'combat' || reason === 'blocked' || reason === 'none') {
      p.path = null;
      if (this.talkId >= 0 && this.h.input.talkTo === this.talkId) this.h.input.talkTo = null;
    }
    this.end();
    this.h.emit({ type: 'navStop', reason });
    if (reason === 'blocked') this.h.notice('หาทางไปไม่พบ');
  }

  private end(): void {
    if (this.running && this.ownRun) this.h.input.sprint = false;
    this.running = false;
    this.state = null;
  }

  private arrive(): void {
    const st = this.state!;
    const p = this.h.world.player;
    this.getOff();
    this.end();
    this.h.emit({ type: 'navArrive', label: st.label, x: p.x, y: p.y });
  }

  /** Gets off a mount the walk put the player on. */
  private getOff(): void {
    if (!this.rode) return;
    this.rode = false;
    if (this.h.world.player.mount) this.act({ t: 'mount' });
  }

  /** Mounts an owned horse for a long leg (the bamboo log on water); a no-op once mounted or when none is owned. */
  private ride(p: Entity, x: number, y: number): void {
    const w = this.h.world;
    if (p.mount || w.interiors.where || p.dead) return;
    const owned = w.progress.mounts;
    const wet = isWaterAt(w.map, x, y) || isWaterAt(w.map, p.x, p.y);
    let id: string | undefined;
    if (wet && owned.includes('bamboo-log')) id = 'bamboo-log';
    else if (!wet && Math.hypot(p.x - x, p.y - y) > MOUNT_LEG)
      id = owned.find((m) => MOUNTS[m]?.move === 'ground' && m.startsWith('horse'));
    if (!id) return;
    if (w.progress.mountPick !== id) this.act({ t: 'setMount', id });
    this.act({ t: 'mount' });
    if (p.mount) this.rode = true;
  }

  private moved(p: Point): void {
    this.fromX = p.x;
    this.fromY = p.y;
    this.bestT = 0;
  }

  /** Runs while stamina lasts, walks while it comes back. */
  private run(p: Entity): void {
    if (!this.ownRun) return;
    const share = p.maxStamina > 0 ? p.stamina / p.maxStamina : 0;
    if (!this.running && share >= RUN_ON) this.running = true;
    else if (this.running && share <= RUN_OFF) this.running = false;
    this.h.input.sprint = this.running;
  }

  /** A command of the walk's own. */
  private act(cmd: PlayerCommand): void {
    this.self = true;
    try {
      this.h.world.apply(cmd);
    } finally {
      this.self = false;
    }
  }

  /** Heads for (x, y) unless already on the way there; false when there is no way. */
  private walk(x: number, y: number): boolean {
    const p = this.h.world.player;
    const fresh = Math.hypot(x - this.aimX, y - this.aimY) > REAIM;
    const stalled = this.bestT > STALL_T;
    if (p.path && !fresh && !stalled) return true;
    if (!fresh) {
      if (++this.stalls > STALL_TRIES) return false;
    } else this.stalls = 0;
    this.aimX = x;
    this.aimY = y;
    this.talkId = -1;
    this.moved(p);
    this.act({ t: 'moveTo', x, y });
    return !!p.path || Math.hypot(p.x - x, p.y - y) < PLACE_REACH;
  }

  /** Looks at the goal and sets the next leg going. */
  private think(): void {
    const st = this.state!;
    const w = this.h.world;
    const p = w.player;
    // Indoors the zone's people and places are set aside: go out first (only a building is looked up inside).
    const where = w.interiors.where;
    const g = where && st.target.kind !== 'building' ? null : this.resolve(st.target);
    if (!g && !where) return this.stop('none');
    if (g) st.label = g.label;
    const set = (leg: NavLeg, x: number, y: number) => {
      st.leg = leg;
      st.x = x;
      st.y = y;
    };
    const go = (leg: NavLeg, x: number, y: number) => {
      set(leg, x, y);
      if (leg !== 'exit' && leg !== 'stairs') this.ride(p, x, y);
      if (!this.walk(x, y)) this.stop('blocked');
    };

    // Indoors: out first, unless this building is the goal.
    if (where) {
      if (g?.kind === 'enter' && g.building === where.building) return this.arrive();
      const f = roomFeatures(w.map);
      let door: { x: number; y: number; at: Point } | null = null;
      for (const dr of f.doors) {
        const at = { x: dr.i + 0.5, y: dr.j + 0.5 };
        if (!door || dist(p, at) < dist(p, door.at)) door = { x: dr.landing.x, y: dr.landing.y, at };
      }
      if (door) {
        if (dist(p, door.at) < DOOR_REACH + 0.3) {
          set('exit', door.x, door.y);
          return this.act({ t: 'exit' });
        }
        return go('exit', door.x, door.y);
      }
      // No door on this floor: the stairs down (or any).
      let best: { x: number; y: number; dir: 1 | -1 } | null = null;
      for (const s of f.stairs) {
        const better =
          !best ||
          (s.dir === -1 && best.dir === 1) ||
          (s.dir === best.dir && dist(p, s.landing) < Math.hypot(p.x - best.x, p.y - best.y));
        if (better) best = { x: s.landing.x, y: s.landing.y, dir: s.dir };
      }
      if (!best) return this.stop('blocked');
      if (Math.hypot(p.x - best.x, p.y - best.y) < DOOR_REACH) {
        set('stairs', best.x, best.y);
        return this.act({ t: 'changeFloor', dir: best.dir });
      }
      return go('stairs', best.x, best.y);
    }

    if (!g) return;
    switch (g.kind) {
      case 'zone': {
        if (this.useWarp && this.paidWarp(g.zone)) return;
        const warp = nextWarp(w.map, g.zone);
        if (!warp) return this.stop('blocked');
        if (!this.hinted && warpDistance(w.map.zone, g.zone) >= CARRIAGE_HOPS) {
          this.hinted = true;
          this.h.notice('ทางไกลหลายด่าน: ใช้รถม้าที่ท่ารถได้');
        }
        // Stepping into the circle carries the player on (the World's warp check); the walk ends with `travel`.
        return go('warp', warp.x, warp.y);
      }
      case 'enter': {
        const door = exteriorDoors(w.map).find((d) => d.building === g.building);
        if (!door) return this.stop('none');
        if (dist(p, door.out) < DOOR_REACH - 0.2) {
          set('door', door.out.x, door.out.y);
          this.getOff();
          return this.act({ t: 'enter', building: g.building });
        }
        return go('door', door.out.x, door.out.y);
      }
      case 'npc': {
        const n = g.e;
        set('talk', n.x, n.y);
        // The World turns the player to them and opens their talk once in range (`talkTo`), and clears it.
        if (this.talkId === n.id && this.h.input.talkTo !== n.id && dist(p, n) < TALK_REACH) return this.arrive();
        const fresh = this.talkId !== n.id || Math.hypot(n.x - this.aimX, n.y - this.aimY) > REAIM;
        if (!fresh && this.h.input.talkTo === n.id && (p.path || dist(p, n) < TALK_REACH) && this.bestT <= STALL_T)
          return;
        if (!fresh && ++this.stalls > STALL_TRIES) return this.stop('blocked');
        if (fresh) this.stalls = 0;
        this.ride(p, n.x, n.y);
        this.talkId = n.id;
        this.aimX = n.x;
        this.aimY = n.y;
        this.moved(p);
        this.act({ t: 'interact', id: n.id });
        if (this.h.input.talkTo !== n.id && !this.state) return;
        if (!p.path && dist(p, n) >= TALK_REACH && this.h.input.talkTo === n.id) return this.stop('blocked');
        return;
      }
      case 'point': {
        if (Math.hypot(p.x - g.x, p.y - g.y) < g.within) {
          p.path = null;
          return this.arrive();
        }
        return go('walk', g.x, g.y);
      }
    }
  }

  /** What a target comes to on the map in play, or null when there is nothing to go to. */
  private resolve(t: NavTarget): Goal | null {
    const w = this.h.world;
    switch (t.kind) {
      case 'quest':
        return this.questGoal();
      case 'npc': {
        const e = w.get(t.id);
        if (!e || e.dead || (e.kind !== 'npc' && !(e.kind === 'ambient' && e.folk))) return null;
        // Someone behind closed doors: their door.
        if (e.folk?.indoors) return { kind: 'point', x: e.x, y: e.y, within: PLACE_REACH, label: e.name };
        return { kind: 'npc', e, label: e.name };
      }
      case 'point':
        return { kind: 'point', x: t.x, y: t.y, within: PLACE_REACH, label: 'จุดหมาย' };
      case 'building':
        return { kind: 'enter', building: t.building, label: 'ประตู' };
      case 'place': {
        const pl = navPlace(t.place);
        if (!pl) return null;
        const s = spotOf(w, pl.at);
        if (!s) return null;
        if ('kind' in s) return { kind: 'npc', e: s, label: pl.name };
        return { kind: 'point', x: s.x, y: s.y, within: PLACE_REACH * 1.5, label: pl.name };
      }
      case 'monster':
        return this.beastGoal(t.monster);
    }
  }

  /** The current quest's next step: its giver to take it or hand it in, else its first unfinished objective. */
  private questGoal(): Goal | null {
    const w = this.h.world;
    const q = w.currentQuest();
    if (!q) return null;
    const qs = w.progress.quest;
    if (qs.status !== 'active') return { kind: 'npc', e: w.elder, label: w.elder.name };
    const i = q.objectives.findIndex((o, k) => (qs.progress[k] ?? 0) < o.count);
    const o = q.objectives[i];
    if (!o) return { kind: 'npc', e: w.elder, label: w.elder.name };
    if (o.type === 'kill' && o.target) {
      const g = this.beastGoal(o.target);
      return g && { ...g, label: o.label };
    }
    // The three forms are learned on a sparring partner, or any bandit.
    for (const id of ['sparrer', 'bandit']) {
      const g = this.beastGoal(id);
      if (g) return { ...g, label: getMonster(id).name };
    }
    return null;
  }

  /** The nearest living beast of a kind here, else where they live here, else the warp toward the nearest zone with them. */
  private beastGoal(id: string): Goal | null {
    const w = this.h.world;
    const p = w.player;
    const name = getMonster(id)?.name ?? id;
    let best: Entity | null = null;
    let bd = Infinity;
    if (!w.interiors.where)
      for (const e of w.entities.values()) {
        if (e.kind !== 'monster' || e.dead || e.brain?.defId !== id) continue;
        const d = dist(p, e);
        if (d < bd) {
          bd = d;
          best = e;
        }
      }
    if (best) return { kind: 'point', x: best.x, y: best.y, within: BEAST_REACH, label: name };
    const here = w.map.zone;
    /** Where they live on a zone's map (looked up on this map only), or null when they do not live there. */
    const home = (z: ZoneId, m: GameMap | null): Point | null => {
      const zd = ZONES[z];
      const sp = zd.spawns.find((s) => s.monster === id);
      if (sp) return m ? centre(m.regions[sp.region]) : ORIGIN;
      if (id === 'worldboss' && zd.kind === 'hub') return m ? m.arena : ORIGIN;
      if (id === 'boss' && (zd.kind === 'hub' || zd.kind === 'village')) return m ? m.lair : ORIGIN;
      return null;
    };
    const at = home(here, w.map);
    if (at) return { kind: 'point', x: at.x, y: at.y, within: BEAST_REACH, label: name };
    // The nearest zone (fewest warps) with them, the hub first on a tie.
    let to: ZoneId | null = null;
    let td = Infinity;
    for (const z of [HUB, ...(Object.keys(ZONES) as ZoneId[])]) {
      if (z === here || !home(z, null)) continue;
      const d = warpDistance(here, z);
      if (d > 0 && d < td) {
        td = d;
        to = z;
      }
    }
    return to ? { kind: 'zone', zone: to, label: `${name} (${ZONES[to].name})` } : null;
  }
}

/** This map's warp that leads fewest warps on to a zone, or null. */
function nextWarp(m: GameMap, to: ZoneId): Point | null {
  let best: Point | null = null;
  let bd = Infinity;
  for (const wp of m.portals) {
    const d = wp.to === to ? 0 : warpDistance(wp.to, to);
    if (d >= 0 && d < bd) {
      bd = d;
      best = wp;
    }
  }
  return best;
}

const dist = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);
const ORIGIN: Point = { x: 0, y: 0 };
